'use client';

import type { Metadata } from 'next';
import { motion } from 'framer-motion';
import Link from 'next/link';

// ─── Quick Navigation Links ───────────────────────────────────────────────────
const QUICK_LINKS = [
  { label: 'Home',     href: '/'         },
  { label: 'About',    href: '/about'    },
  { label: 'Services', href: '/services' },
  { label: 'Blog',     href: '/blog'     },
  { label: 'Contact',  href: '/contact'  },
];

// ─── Animation Variants ───────────────────────────────────────────────────────
const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.55, ease: [0.42, 0, 0.58, 1] as const },
});

// ─── Satellite Dish Illustration (SVG) ───────────────────────────────────────
function SatelliteIllustration() {
  return (
    <svg
      viewBox="0 0 340 240"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full"
      aria-hidden="true"
    >
      {/* ── Stand ── */}
      <rect x="160" y="162" width="18" height="52" rx="4" fill="#1A3A6A" />
      <rect x="128" y="206" width="84" height="12" rx="6" fill="#1A3A6A" />

      {/* ── Dish Bowl ── */}
      <path
        d="M72 138 Q169 58 266 138"
        stroke="#1A6FCC"
        strokeWidth="7"
        strokeLinecap="round"
        fill="none"
      />
      <path
        d="M92 148 Q169 82 246 148"
        stroke="rgba(26,111,204,0.18)"
        strokeWidth="3"
        strokeLinecap="round"
        fill="rgba(26,111,204,0.05)"
      />

      {/* ── Arm ── */}
      <line
        x1="169" y1="100"
        x2="169" y2="162"
        stroke="#1A6FCC"
        strokeWidth="5"
        strokeLinecap="round"
      />

      {/* ── Feed Horn ── */}
      <circle cx="169" cy="97" r="9" fill="#C8601A" />
      <circle cx="169" cy="97" r="5" fill="rgba(255,255,255,0.15)" />

      {/* ── Broken Signal Arcs (dashed) ── */}
      <path
        d="M196 72 Q218 50 196 28"
        stroke="#C8601A"
        strokeWidth="3.5"
        strokeLinecap="round"
        strokeDasharray="7 5"
        opacity="0.95"
      />
      <path
        d="M214 83 Q246 50 214 18"
        stroke="#C8601A"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeDasharray="6 6"
        opacity="0.55"
      />
      <path
        d="M232 94 Q274 50 232 8"
        stroke="#C8601A"
        strokeWidth="2"
        strokeLinecap="round"
        strokeDasharray="5 7"
        opacity="0.25"
      />

      {/* ── No-Signal Badge ── */}
      <g transform="translate(270, 12)">
        <circle cx="16" cy="16" r="16"
          fill="rgba(200,96,26,0.12)"
          stroke="#C8601A"
          strokeWidth="1.5"
        />
        <line x1="10" y1="10" x2="22" y2="22"
          stroke="#C8601A" strokeWidth="2.5" strokeLinecap="round"
        />
        <line x1="22" y1="10" x2="10" y2="22"
          stroke="#C8601A" strokeWidth="2.5" strokeLinecap="round"
        />
      </g>

      {/* ── Ambient Particles ── */}
      <rect x="44"  y="54"  width="9" height="9" rx="2" fill="#1A6FCC" opacity="0.40" />
      <rect x="26"  y="90"  width="7" height="7" rx="2" fill="#1A6FCC" opacity="0.22" />
      <rect x="288" y="118" width="8" height="8" rx="2" fill="#C8601A" opacity="0.35" />
      <rect x="56"  y="120" width="5" height="5" rx="1" fill="#1A6FCC" opacity="0.18" />
    </svg>
  );
}

// ─── Dot-Grid + Radial Glow Background ───────────────────────────────────────
function BackgroundLayer() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Dot grid */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.045]" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="dotGrid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
            <circle cx="1" cy="1" r="1" fill="#1A6FCC" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dotGrid)" />
      </svg>

      {/* Blue radial glow — top-left */}
      <div className="absolute -top-48 -left-48 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,_rgba(26,111,204,0.13)_0%,_transparent_70%)]" />

      {/* Orange radial glow — bottom-right */}
      <div className="absolute -bottom-48 -right-48 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,_rgba(200,96,26,0.10)_0%,_transparent_70%)]" />

      {/* Horizontal accent line */}
      <div className="absolute top-1/2 left-0 right-0 h-px bg-[linear-gradient(90deg,_transparent_0%,_rgba(26,111,204,0.14)_30%,_rgba(200,96,26,0.12)_70%,_transparent_100%)]" />
    </div>
  );
}

export const metadata: Metadata = {
  title: '404 Not Found | Farsight Solutions',
  description:
    'The page you requested was not found. Return to Farsight Solutions home or contact support for help.',
};

// ─── Page Component ───────────────────────────────────────────────────────────
export default function NotFound() {
  return (
    <main className="relative min-h-screen flex items-center justify-center px-6 py-20 overflow-hidden bg-notfound">
      <BackgroundLayer />

      <div className="relative z-10 w-full max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">

        {/* ── Left Column — Content ─────────────────────────────── */}
        <div className="order-2 lg:order-1">

          {/* Error Badge */}
          <motion.div
            {...fadeUp(0)}
            className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full mb-7 bg-[rgba(200,96,26,0.08)] border-[1px_solid_rgba(200,96,26,0.28)]"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-70 bg-[#C8601A]" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#C8601A]" />
            </span>
            <span className="text-xs font-semibold tracking-[0.16em] uppercase text-[#C8601A]">
              Error 404
            </span>
          </motion.div>

          {/* 404 Numeral */}
          <div className="relative mb-3 leading-none select-none">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.75 }}
              className="block font-black text-[clamp(88px,_14vw,_152px)] leading-none text-gradient-404 drop-shadow-[0_0_36px_rgba(26,111,204,0.28)]"
            >
              404
            </motion.span>

            {/* Shadow echo */}
            <span
              className="absolute inset-0 block font-black text-[clamp(88px,_14vw,_152px)] leading-none text-stroke-404 translate-x-[5px] translate-y-[5px] pointer-events-none"
              aria-hidden="true"
            >
              404
            </span>
          </div>

          {/* Heading */}
          <motion.h1
            {...fadeUp(0.2)}
            className="text-3xl md:text-[2.6rem] font-bold leading-tight mb-4 text-[#EFF4FF] font-sora"
          >
            Page Not Found
          </motion.h1>

          {/* Description */}
          <motion.p
            {...fadeUp(0.3)}
            className="text-base md:text-lg leading-relaxed max-w-md mb-10 text-[rgba(255,255,255,0.42)]"
          >
            The page you are looking for may have been moved, renamed, or is
            temporarily unavailable. Please use the options below to navigate
            back to a valid destination.
          </motion.p>

          {/* Action Buttons */}
          <motion.div
            {...fadeUp(0.4)}
            className="flex flex-wrap gap-4 mb-12"
          >
            {/* Primary — Back to Home */}
            <Link
            href="/"
            className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-lg text-sm font-semibold transition-all duration-200 hover:-translate-y-1 bg-[linear-gradient(135deg,_#1A6FCC_0%,_#155BAD_100%)] text-white border-[1px_solid_rgba(26,111,204,0.50)] shadow-[0_4px_22px_rgba(26,111,204,0.32)]"
          >
            {/* Home Icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
            Back to Home
          </Link>

            {/* Secondary — Contact Support */}
            <Link
              href="/contact"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-lg text-sm font-semibold transition-all duration-200 hover:-translate-y-1 bg-[rgba(200,96,26,0.07)] text-[#E8833A] border-[1px_solid_rgba(200,96,26,0.28)]"
            >
              {/* Message Icon */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
              Contact Support
            </Link>
          </motion.div>

          {/* Quick Navigation */}
          <motion.div
            {...fadeUp(0.5)}
            className="pt-8 border-t border-[rgba(26,111,204,0.10)]"
          >
            <p className="text-xs font-semibold tracking-[0.14em] uppercase mb-4 text-[rgba(255,255,255,0.22)]">
              Quick Navigation
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {QUICK_LINKS.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium transition-colors duration-150 hover:text-[#60A5FA] text-[rgba(255,255,255,0.38)]"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Right Column — Illustration ───────────────────────── */}
        <motion.div
          className="order-1 lg:order-2 flex justify-center"
          initial={{ opacity: 0, scale: 0.88 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, duration: 0.65, ease: 'easeOut' }}
        >
          <div className="relative w-full max-w-[380px]">

            {/* Ambient glow ring */}
            <div className="absolute inset-0 rounded-full pointer-events-none bg-[radial-gradient(circle,_rgba(26,111,204,0.09)_0%,_transparent_72%)] scale-[1.3]" aria-hidden="true" />

            {/* Floating illustration */}
            <motion.div
              animate={{ y: [0, -16, 0] }}
              transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut' }}
              className="relative px-6 py-8"
            >
              <SatelliteIllustration />
            </motion.div>

            {/* Status Card — bottom-right */}
            <motion.div
              initial={{ opacity: 0, x: 18 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.65, duration: 0.5 }}
              className="absolute -bottom-2 -right-2 px-4 py-3 rounded-xl bg-[rgba(10,17,34,0.92)] border-[1px_solid_rgba(26,111,204,0.22)] backdrop-blur-[14px] shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-[rgba(200,96,26,0.14)]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="#C8601A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold leading-tight text-[#EFF4FF]">
                    Signal Lost
                  </p>
                  <p className="text-xs leading-tight mt-0.5 text-[rgba(255,255,255,0.38)]">
                    Destination unreachable
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Status Card — top-left */}
            <motion.div
              initial={{ opacity: 0, x: -18 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.75, duration: 0.5 }}
              className="absolute -top-2 -left-2 px-4 py-3 rounded-xl bg-[rgba(10,17,34,0.92)] border-[1px_solid_rgba(26,111,204,0.22)] backdrop-blur-[14px] shadow-[0_8px_32px_rgba(0,0,0,0.45)]"
            >
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-[rgba(26,111,204,0.14)]">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="#1A6FCC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-semibold leading-tight text-[#EFF4FF]">
                    Scanning Routes
                  </p>
                  <p className="text-xs leading-tight mt-0.5 text-[rgba(255,255,255,0.38)]">
                    No match found
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </motion.div>

      </div>
    </main>
  );
}